// Export dropdown used in every report page header.
//
// Exposed formats depend on user role:
//   - MANAGER+ : CSV (always)
//   - MANAGER+ : WhatsApp / Email (per §10.2 Report Share)
//   - OWNER+   : PDF (consolidated reports, locked behind higher role)
//
// The component is intentionally presentational — CSV rendering and share
// wiring are supplied by the caller so each report can format its own
// payload.

import { useCallback, useState } from 'react';
import { Button } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';

export type ReportExportFormat = 'csv' | 'pdf' | 'whatsapp' | 'email';

export interface ReportExportMenuProps {
  disabled?: boolean;
  onExport: (format: ReportExportFormat) => void;
  /** Allow overriding the default format set (e.g. exclude PDF). */
  formats?: ReportExportFormat[];
}

const ROLE_LEVELS: Record<string, number> = {
  SUPER_ADMIN: 100,
  ADMIN: 80,
  MODERATOR: 60,
  OWNER: 50,
  MANAGER: 30,
  CASHIER: 5,
};

const FORMAT_LABEL: Record<ReportExportFormat, string> = {
  csv: 'Export CSV',
  pdf: 'Download PDF',
  whatsapp: 'Share on WhatsApp',
  email: 'Share via Email',
};

const FORMAT_ICON: Record<ReportExportFormat, React.ReactNode> = {
  csv: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  ),
  pdf: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-5a1 1 0 011-1h2a1 1 0 011 1v5m-7 4h10a2 2 0 002-2V7.414a1 1 0 00-.293-.707l-4.414-4.414A1 1 0 0013.586 2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  whatsapp: (
    <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M20.52 3.48A12 12 0 003.48 20.52L2 22l1.55-1.44A12 12 0 1020.52 3.48zM12 20.27a8.2 8.2 0 01-4.18-1.14l-.3-.18-3.08.81.82-3-.2-.31a8.21 8.21 0 1113.11 6.62A8.13 8.13 0 0112 20.27z" />
    </svg>
  ),
  email: (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z" />
    </svg>
  ),
};

export function ReportExportMenu({
  disabled,
  onExport,
  formats,
}: ReportExportMenuProps) {
  const [open, setOpen] = useState(false);
  const role = useAuthStore((s) => s.profile?.role ?? '');
  const level = ROLE_LEVELS[role] ?? 0;

  // Default menu: CSV + Share channels for MANAGER+, PDF for OWNER+
  const defaultFormats: ReportExportFormat[] = ['csv'];
  if (level >= 30) defaultFormats.push('whatsapp', 'email');
  if (level >= 50) defaultFormats.splice(1, 0, 'pdf');

  const availableFormats = (formats ?? defaultFormats).filter((f) => {
    if (f === 'pdf') return level >= 50;
    if (f === 'whatsapp' || f === 'email') return level >= 30;
    return true;
  });

  const handleExport = useCallback(
    (fmt: ReportExportFormat) => {
      setOpen(false);
      onExport(fmt);
    },
    [onExport],
  );

  return (
    <div className="relative">
      <Button
        variant="secondary"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm"
      >
        <span className="flex items-center gap-2">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Export
          <svg className="h-3 w-3 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>
      </Button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close export menu"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            role="menu"
            className="absolute right-0 z-50 mt-2 w-56 rounded-xl bg-white dark:bg-neutral-800 shadow-xl ring-1 ring-neutral-200 dark:ring-neutral-700 overflow-hidden animate-scale-in"
          >
            {availableFormats.map((fmt) => (
              <button
                key={fmt}
                type="button"
                role="menuitem"
                onClick={() => handleExport(fmt)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-brand-50 dark:hover:bg-brand-900/30 transition-colors"
              >
                <span className="text-brand-500">{FORMAT_ICON[fmt]}</span>
                <span>{FORMAT_LABEL[fmt]}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
